<template>
  <div class="word-reading">
    <div
      v-if="showMicPermissionBanner"
      class="mic-banner"
      role="alert"
      aria-live="assertive"
    >
      <div class="mic-banner-content">
        <strong>🎤 Activar permiso de micrófono</strong>
        <p class="mic-banner-text">
          Para continuar, necesitas permitir el acceso al micrófono.
          <br />
          Si antes presionaste <b>No permitir</b>, abre el ícono del
          <b>candado</b> o los <b>ajustes del sitio</b> junto a la URL y cambia
          el micrófono a <b>Permitir</b>.
        </p>
        <p class="mic-banner-help">
          {{ browserMicInstructions }}
        </p>

        <div class="mic-banner-actions">
          <button
            class="secondary-btn"
            :disabled="isPermissionRequesting"
            @click="retryMicrophonePermission"
          >
            {{
              isPermissionRequesting
                ? "Comprobando..."
                : "Reintentar micrófono"
            }}
          </button>

          <button class="secondary-btn" @click="reloadPage">
            Recargar página
          </button>
        </div>
      </div>
    </div>

    <div v-if="isGameComplete">
      <LevelComplete />
    </div>

    <div v-else>
      <h1
        class="target-word"
        title="Click to hear the word"
        @click="speakOutLoud(targetWord)"
      >
        {{ currentEmoji }}
      </h1>

      <p 
        class="target-word-text"
        @click="speakOutLoud(targetWord)"
      >
        {{ targetWord }}
      </p>

      <button
        :disabled="isListening || isPermissionRequesting"
        @click="startRecognition"
      >
        {{
          isListening
            ? "Listening..."
            : isPermissionRequesting
            ? "Checking mic..."
            : "Speak 🎙️"
        }}
      </button>

      <p v-if="message" class="message">
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin";

export default {
  name: "WordReading",
  mixins: [speechMixin],
  components: {
    LevelComplete,
  },
  data() {
    return {
      words: [
        "cat",
        "ball",
        "moon",
        "star",
        "fish",
        "bird",
        "milk",
        "car",
        "bus",
        "apple",
        "tree",
        "orange",
      ],
      emojiMap: {
        cat: "🐈",
        ball: "⚽",
        moon: "🌙",
        star: "⭐",
        fish: "🐟",
        bird: "🐦",
        milk: "🥛",
        car: "🚗",
        bus: "🚌",
        apple: "🍎",
        tree: "🌳",
        orange: "🍊",
      },
      targetWord: "",
      message: "",
      isListening: false,
      isPermissionRequesting: false,
      showMicPermissionBanner: false,
      wordCounts: {},
      maxCount: 1,
      recognition: null,
      isGameComplete: false,
      permissionStatus: null,
      messageTimeout: null,
      resetTimeout: null,
      redirectTimeout: null,
    };
  },
  computed: {
    currentEmoji() {
      return this.emojiMap[this.targetWord] || "🔊";
    },
    browserMicInstructions() {
      const ua = navigator.userAgent || "";

      if (ua.includes("Chrome") && !ua.includes("Edg")) {
        return "Chrome: toca el candado al lado de la dirección web → Micrófono → Permitir.";
      }

      if (ua.includes("Edg")) {
        return "Edge: toca el candado al lado de la dirección web → Permisos para este sitio → Micrófono → Permitir.";
      }

      if (ua.includes("Firefox")) {
        return "Firefox: toca el candado al lado de la dirección web y habilita el micrófono para este sitio.";
      }

      if (ua.includes("Safari") && !ua.includes("Chrome")) {
        return "Safari: revisa la configuración del sitio web y permite el acceso al micrófono.";
      }

      return "Busca el candado o configuración del sitio junto a la barra de direcciones y permite el micrófono.";
    },
  },
  mounted() {
    this.initializeWordCounts();
    this.setupGame();
    this.setupRecognition();
    this.watchMicrophonePermission();

    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = this.setupVoices;
      this.setupVoices();
    }
  },
  beforeUnmount() {
    this.cleanupComponent();
  },
  methods: {
    cleanupComponent() {
      this.clearAllTimers();

      if (this.permissionStatus) {
        this.permissionStatus.onchange = null;
      }

      if (this.recognition) {
        try {
          this.recognition.onresult = null;
          this.recognition.onerror = null;
          this.recognition.onend = null;
          this.recognition.onstart = null;
          this.recognition.abort();
        } catch (error) {
          console.error("Error cleaning up recognition:", error);
        }
      }

      if (
        window.speechSynthesis &&
        window.speechSynthesis.onvoiceschanged === this.setupVoices
      ) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    },

    clearAllTimers() {
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
        this.messageTimeout = null;
      }

      if (this.resetTimeout) {
        clearTimeout(this.resetTimeout);
        this.resetTimeout = null;
      }

      if (this.redirectTimeout) {
        clearTimeout(this.redirectTimeout);
        this.redirectTimeout = null;
      }
    },

    setTemporaryMessage(text, duration = 3000) {
      this.message = text;

      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
      }

      this.messageTimeout = setTimeout(() => {
        this.message = "";
        this.messageTimeout = null;
      }, duration);
    },

    initializeWordCounts() {
      this.wordCounts = this.words.reduce((acc, word) => {
        acc[word] = 0;
        return acc;
      }, {});
    },

    setupGame() {
      if (this.isGameComplete) return;

      const availableWords = this.words.filter(
        (word) => (this.wordCounts[word] || 0) < this.maxCount
      );

      if (availableWords.length === 0) {
        this.isGameComplete = true;
        this.targetWord = "";
        this.message = "";

        this.playSound(
          "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
        );
        this.speakOutLoud("Great Job!");

        if (this.redirectTimeout) {
          clearTimeout(this.redirectTimeout);
        }

        this.redirectTimeout = setTimeout(() => {
          this.redirectTimeout = null;
          if (this.$router) {
            this.$router.push("/words");
          }
        }, 4000);

        return;
      }

      const randomIndex = Math.floor(Math.random() * availableWords.length);
      this.targetWord = availableWords[randomIndex];
      this.message = "";

      this.speakOutLoud(this.targetWord);
    },

    setupRecognition() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        this.showMicPermissionBanner = true;
        this.setTemporaryMessage(
          "Tu navegador no soporta reconocimiento de voz."
        );
        return;
      }

      this.recognition = new SpeechRecognition();
      this.recognition.lang = "en-US";
      this.recognition.interimResults = false;
      this.recognition.maxAlternatives = 1;

      this.recognition.onstart = () => {
        this.isListening = true;
      };

      this.recognition.onresult = (event) => {
        this.handleResult(event);
      };

      this.recognition.onerror = (event) => {
        console.error("Speech recognition error:", event);
        this.isListening = false;

        const errorType = event && event.error ? event.error : "";

        if (
          errorType === "not-allowed" ||
          errorType === "service-not-allowed"
        ) {
          this.handleMicrophonePermissionDenied();
          return;
        }

        if (errorType === "audio-capture") {
          this.showMicPermissionBanner = true;
          this.setTemporaryMessage("No se detectó un micrófono disponible.");
          return;
        }

        if (errorType === "no-speech") {
          this.setTemporaryMessage("No te escuché. Inténtalo otra vez.");
          return;
        }

        this.setTemporaryMessage(
          "Ocurrió un error durante el reconocimiento de voz."
        );
      };

      this.recognition.onend = () => {
        this.isListening = false;
      };
    },

    async watchMicrophonePermission() {
      if (!navigator.permissions || !navigator.permissions.query) {
        return;
      }

      try {
        const status = await navigator.permissions.query({
          name: "microphone",
        });

        this.permissionStatus = status;
        this.applyPermissionState(status.state);

        status.onchange = () => {
          this.applyPermissionState(status.state);
        };
      } catch (error) {
        // Some browsers do not support querying microphone permission
        console.warn("Microphone permission query not supported:", error);
      }
    },

    applyPermissionState(state) {
      if (state === "denied") {
        this.showMicPermissionBanner = true;
      } else if (state === "granted") {
        this.showMicPermissionBanner = false;
      }
    },

    handleMicrophonePermissionDenied() {
      this.showMicPermissionBanner = true;
      this.isListening = false;
      this.setTemporaryMessage(
        "Micrófono bloqueado. Activa el permiso en tu navegador."
      );
    },

    async ensureMicrophonePermission() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.showMicPermissionBanner = true;
        this.setTemporaryMessage("Tu navegador no permite acceder al micrófono.");
        return false;
      }

      this.isPermissionRequesting = true;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        stream.getTracks().forEach((track) => track.stop());

        this.showMicPermissionBanner = false;
        this.isPermissionRequesting = false;
        return true;
      } catch (error) {
        console.error("getUserMedia error:", error);

        this.isPermissionRequesting = false;

        const errorName = error && error.name ? error.name : "";

        if (
          errorName === "NotAllowedError" ||
          errorName === "PermissionDeniedError" ||
          errorName === "SecurityError"
        ) {
          this.handleMicrophonePermissionDenied();
          return false;
        }

        if (
          errorName === "NotFoundError" ||
          errorName === "DevicesNotFoundError"
        ) {
          this.showMicPermissionBanner = true;
          this.setTemporaryMessage("No se encontró ningún micrófono.");
          return false;
        }

        this.showMicPermissionBanner = true;
        this.setTemporaryMessage("No se pudo acceder al micrófono.");
        return false;
      }
    },

    async startRecognition() {
      if (this.isListening || this.isGameComplete) return;

      if (!this.recognition) {
        this.setupRecognition();
      }

      if (!this.recognition) return;

      this.message = "";

      const hasPermission = await this.ensureMicrophonePermission();
      if (!hasPermission) return;

      try {
        this.recognition.start();
      } catch (error) {
        console.error("recognition.start error:", error);

        if (error && error.name === "InvalidStateError") {
          return;
        }

        this.isListening = false;
        this.setTemporaryMessage(
          "No se pudo iniciar el micrófono. Inténtalo otra vez."
        );
      }
    },

    async retryMicrophonePermission() {
      const hasPermission = await this.ensureMicrophonePermission();

      if (hasPermission) {
        this.setTemporaryMessage("Micrófono activado correctamente.", 2000);
      }
    },

    normalizeText(text) {
      return String(text || "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s]/g, "")
        .replace(/\s+/g, " ");
    },

    handleResult(event) {
      if (
        !event ||
        !event.results ||
        !event.results[0] ||
        !event.results[0][0]
      ) {
        this.setTemporaryMessage("No pude entender lo que dijiste.");
        return;
      }

      const rawSpokenText = event.results[0][0].transcript || "";
      const spokenText = this.normalizeText(rawSpokenText);
      const targetText = this.normalizeText(this.targetWord);

      if (!targetText) return;

      if (spokenText === targetText) {
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );

        this.message = "✅ Correct!";
        this.wordCounts[this.targetWord] =
          (this.wordCounts[this.targetWord] || 0) + 1;

        if (this.resetTimeout) {
          clearTimeout(this.resetTimeout);
        }

        this.resetTimeout = setTimeout(() => {
          this.resetTimeout = null;
          this.resetGame();
        }, 1500);
      } else {
        this.setTemporaryMessage(`❌ Incorrecto. Dijiste "${rawSpokenText}"`);
      }
    },

    playSound(url) {
      try {
        const audio = new Audio(url);
        audio.play().catch((error) => {
          console.warn("Audio play was blocked:", error);
        });
      } catch (error) {
        console.error("Error playing sound:", error);
      }
    },

    resetGame() {
      this.setupGame();
    },

    reloadPage() {
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.word-reading {
  text-align: center;
  padding: 20px;
}

.mic-banner {
  margin: 0 auto 20px;
  max-width: 720px;
  background: #fff3cd;
  border: 1px solid #ffe08a;
  color: #7a5a00;
  border-radius: 14px;
  padding: 16px;
  text-align: left;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.mic-banner-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mic-banner-text,
.mic-banner-help {
  margin: 0;
  line-height: 1.5;
}

.mic-banner-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

.target-word {
  font-size: 4rem;
  color: #ff4081;
  margin-bottom: 10px;
  cursor: pointer;
  transition: transform 0.3s;
}

.target-word:hover {
  transform: scale(1.05);
}

.target-word-text {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

button {
  font-size: 1.2rem;
  padding: 10px 20px;
  background-color: #81d4fa;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

button:hover {
  background-color: #4fc3f7;
}

button:disabled {
  background-color: #cfd8dc;
  cursor: not-allowed;
}

.secondary-btn {
  background-color: #ffd54f;
  color: #333;
}

.secondary-btn:hover {
  background-color: #ffca28;
}

.message {
  font-size: 1.5rem;
  font-weight: bold;
  margin-top: 20px;
}
</style>